import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey, type EditorState } from '@atlaskit/editor-prosemirror/state';

export type ThinkingStatus = 'idle' | 'thinking' | 'complete' | 'error';

/** Error taxonomy, aligned to inline chat's `message_template`. `UNKNOWN` is the fallback. */
export type ThinkingErrorTemplate =
	| 'RATE_LIMIT'
	| 'ACCEPTABLE_USE_VIOLATIONS'
	| 'TOKEN_LIMIT_EXCEEDED'
	| 'UNKNOWN';

export type ThinkingError = {
	template: ThinkingErrorTemplate;
};

export type ReviewResults = {
	count: number;
	hadExistingSuggestions: boolean;
};

/**
 * Identity of the agent handling the review, as resolved by
 * `useContentReviewerAgentResolver`. Held in plugin state so every surface — the editor
 * thinking bar and the product's own loading states — can name the agent without each
 * resolving it for itself.
 */
export type ReviewingAgent = {
	id: string;
	/** Rovo identity account id, for the agent's avatar. */
	identityAccountId?: string | null;
	name: string;
	/** `external_config_reference ?? named_id`, for the agent's avatar. */
	namedId?: string;
};

export type ThinkingPluginState = {
	baselineIds?: readonly string[];
	/** Set only when status is 'error'; drives the error screen. `null` otherwise. */
	error: ThinkingError | null;
	/**
	 * Progress-bar facade target (0-100) chosen once per thinking session. Kept in
	 * state (rather than randomised per component mount) so all bars fill to the
	 * same value. `null` while idle.
	 */
	progressTarget: number | null;
	/**
	 * Agent handling the review, or `null` while it hasn't resolved. Not session-scoped:
	 * the agent is a property of the site, so once resolved it's kept across sessions
	 * (it's also resolved ahead of the first session, so the first status can name it).
	 */
	reviewingAgent: ReviewingAgent | null;
	reviewResults?: ReviewResults;
	/**
	 * Epoch ms captured when the current thinking session started (status → 'thinking').
	 * Shared by every loading bar so their progress/rotation animations stay in sync
	 * across mount/unmount (e.g. switching between the editor bar and the sidebar bar).
	 * `null` while idle.
	 */
	startedAt: number | null;
	status: ThinkingStatus;
};

/**
 * A partial update: every field is optional so a transaction can carry a status
 * transition, the resolved agent, or both.
 */
export type ThinkingPluginMeta = {
	error?: ThinkingError;
	reviewingAgent?: ReviewingAgent | null;
	status?: ThinkingStatus;
};

// Facade progress hovers at a randomised target (85-95%) until completion.
const pickProgressTarget = (): number => 85 + Math.random() * 10;

export const thinkingPluginKey: PluginKey<ThinkingPluginState> = new PluginKey<ThinkingPluginState>(
	'aiSuggestionsThinkingPlugin',
);

export function createThinkingPlugin({
	dispatch,
	getSuggestionIds,
}: {
	dispatch: Dispatch;
	getSuggestionIds?: (state: EditorState) => readonly string[] | undefined;
}): SafePlugin<ThinkingPluginState> {
	return new SafePlugin<ThinkingPluginState>({
		key: thinkingPluginKey,
		state: {
			init(): ThinkingPluginState {
				return {
					status: 'idle',
					startedAt: null,
					progressTarget: null,
					error: null,
					reviewingAgent: null,
				};
			},
			apply(tr, pluginState, oldState, newState): ThinkingPluginState {
				let result = pluginState;
				const meta: ThinkingPluginMeta | undefined = tr.getMeta(thinkingPluginKey);

				const isStatusChange =
					!!meta && meta.status !== undefined && meta.status !== pluginState.status;
				const isAgentChange =
					!!meta &&
					meta.reviewingAgent !== undefined &&
					meta.reviewingAgent !== pluginState.reviewingAgent;

				if (meta && (isStatusChange || isAgentChange)) {
					let {
						status,
						startedAt,
						progressTarget,
						error,
						reviewingAgent,
						baselineIds,
						reviewResults,
					} = pluginState;

					if (isStatusChange && meta.status) {
						status = meta.status;

						if (status === 'thinking') {
							// Start of a new session: anchor the shared timing/target once.
							startedAt = Date.now();
							progressTarget = pickProgressTarget();
							baselineIds = getSuggestionIds?.(oldState);
						} else if (status === 'idle') {
							// Session ended/cancelled: clear so the next session starts fresh.
							startedAt = null;
							progressTarget = null;
							baselineIds = undefined;
						}
						// 'complete' and 'error' keep startedAt/progressTarget so the bar can
						// settle its progress before showing the ready / error state.

						// A status transition starts (or ends) a session, so the previous
						// session's results no longer apply.
						reviewResults = undefined;

						// Carry the error payload only into the 'error' state; clear otherwise.
						error = status === 'error' ? (meta.error ?? { template: 'UNKNOWN' }) : null;
					}

					if (isAgentChange) {
						reviewingAgent = meta.reviewingAgent ?? null;
					}

					result = {
						baselineIds,
						reviewResults,
						status,
						startedAt,
						progressTarget,
						error,
						reviewingAgent,
					};
				}

				const suggestionIds = getSuggestionIds?.(newState);
				if (result.status === 'thinking' && !result.baselineIds && suggestionIds) {
					result = { ...result, baselineIds: suggestionIds };
				}
				// Refreshes already update suggestions asynchronously after completion.
				// Keep the count in sync with that existing flow without delaying the bar.
				if (
					result.status === 'complete' &&
					result.baselineIds &&
					suggestionIds &&
					(!result.reviewResults || suggestionIds !== getSuggestionIds?.(oldState))
				) {
					const baseline = new Set(result.baselineIds);
					const count = suggestionIds.filter((id) => !baseline.has(id)).length;
					if (result.reviewResults?.count !== count) {
						result = {
							...result,
							reviewResults: { count, hadExistingSuggestions: baseline.size > 0 },
						};
					}
				}
				if (result !== pluginState) {
					dispatch(thinkingPluginKey, result);
				}
				return result;
			},
		},
	});
}
